/* =============================================================
   AMENITIES
   =============================================================
   Labels for the `amenity_code` enum.

   These live here rather than in a database table on purpose,
   and it is the opposite call from countries.ts — so the
   difference is worth stating.

   `berths.country_code` is a FOREIGN KEY to public.countries, so
   that table has to exist: it is the validator, and fetching the
   rows means the dropdown cannot offer a country the database
   would reject. Amenities are not like that. Postgres has no
   foreign key on array elements, which is exactly why
   `berths.amenities` is an array of an ENUM — the type is the
   validator, all by itself. A table alongside it would hold only
   the words, and the words are the part that gets translated.

   `landing/` already ships in en/de/hu. When the app follows,
   amenity names come from translation files keyed by these
   codes, and a `label` column in Postgres would be a second copy
   nobody renders. One source, on the client, next to every other
   string.

   THE GUARANTEE: `Record<AmenityCode, string>` is exhaustive.
   Add a value to `amenity_code` and to the `AmenityCode` union
   without adding a label here and the build fails — which beats
   the runtime check this replaced, that only spoke up when
   somebody happened to run `sb status`.
   ============================================================= */

import type { Amenity, AmenityCode } from './types'

/**
 * Enum declaration order, which is display order — `order by code` in SQL
 * sorts the same way, so a report and this list agree without either knowing
 * about the other.
 */
export const AMENITY_ORDER: AmenityCode[] = ['water', 'waste', 'fuel', 'ops', 'loading_area']

const LABELS: Record<AmenityCode, string> = {
  water: 'Water supply',
  waste: 'Waste & sewage',
  fuel: 'Bunker fuel',
  ops: 'Shore power (OPS)',
  loading_area: 'Loading area',
}

/** The five, in display order — what a picker iterates. */
export const AMENITIES: Amenity[] = AMENITY_ORDER.map((code) => ({ code, label: LABELS[code] }))

/**
 * No init, no fetch, no failure state: the labels are compiled in, so unlike
 * useCountries() this cannot be empty or late. Kept as a hook-shaped export so
 * the call sites read the same as the country ones.
 */
export function useAmenities() {
  return {
    amenities: AMENITIES,
    amenityLabel: (code: AmenityCode): string => LABELS[code] ?? code,
  }
}
